import {
  differenceInDays,
  format,
  isValid,
  parse,
  setHours,
  setMinutes,
  subDays,
  subWeeks,
} from 'date-fns';
import { Check, X } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import { cn } from '@/lib/utils';
import { usePatchMatterMutation } from '@/services/api/mattersService';

import { InfoBadge } from './InfoBadge';

interface LastContactEditBadgeProps {
  matterId: string;
  value: string | undefined | null;
}

const formatDateForDisplay = (
  dateString: string | undefined | null,
): string => {
  if (!dateString) return '';

  try {
    const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) {
      return format(parsed, 'MM/dd/yyyy');
    }

    const date = new Date(dateString);
    if (isValid(date)) {
      return format(date, 'MM/dd/yyyy');
    }
  } catch {
    // Silent catch
  }

  return dateString;
};

const getDaysSinceLastContact = (
  dateString: string | undefined | null,
): number | null => {
  if (!dateString) return null;
  try {
    const lastContactDate = new Date(dateString);
    if (isValid(lastContactDate)) {
      return differenceInDays(new Date(), lastContactDate);
    }
  } catch {
    // Silent catch
  }
  return null;
};

const getLastContactDisplayText = (
  daysSinceLastContact: number | null,
): string | null => {
  if (daysSinceLastContact === null) return null;

  if (daysSinceLastContact === 0) {
    return 'Today';
  }
  if (daysSinceLastContact === 1) {
    return 'Yesterday';
  }
  return `${daysSinceLastContact} days`;
};

const getLastContactStyles = (daysSinceLastContact: number | null) => {
  if (daysSinceLastContact === null) {
    return {
      className:
        'bg-transparent hover:bg-zinc-50 border-transparent hover:border-zinc-100',
      valueClassName: undefined,
    };
  }

  if (daysSinceLastContact >= 30) {
    return {
      className: 'bg-red-50 border border-red-100 hover:bg-red-100',
      valueClassName: 'text-red-700',
    };
  }

  if (daysSinceLastContact >= 14) {
    return {
      className: 'bg-amber-50 border border-amber-100 hover:bg-amber-100',
      valueClassName: 'text-amber-700',
    };
  }

  return {
    className: 'bg-green-50 border border-green-100 hover:bg-green-100',
    valueClassName: 'text-green-700',
  };
};

const QuickDateButton: React.FC<{
  label: string;
  onClick: () => void;
  isSelected: boolean;
}> = ({ label, onClick, isSelected }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'px-1.5 py-0.5 text-[10px] font-medium rounded transition-all',
      isSelected
        ? 'bg-zinc-900 text-white'
        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
    )}
  >
    {label}
  </button>
);

const TimeSelect: React.FC<{
  value: number;
  onChange: (value: number) => void;
  options: number[];
  formatFn?: (v: number) => string;
}> = ({ value, onChange, options, formatFn }) => (
  <select
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    className="h-6 px-1 text-[11px] bg-white border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-400 appearance-none cursor-pointer"
  >
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {formatFn ? formatFn(opt) : opt}
      </option>
    ))}
  </select>
);

export const LastContactEditBadge: React.FC<LastContactEditBadgeProps> = ({
  matterId,
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    if (!value) return undefined;
    const date = new Date(value);
    return isValid(date) ? date : undefined;
  });
  const [patchMatter, { isLoading }] = usePatchMatterMutation();

  const daysSinceLastContact = getDaysSinceLastContact(value);
  const lastContactDisplayText =
    getLastContactDisplayText(daysSinceLastContact);
  const lastContactFullDate = formatDateForDisplay(value);
  const lastContactStyles = getLastContactStyles(daysSinceLastContact);

  // Time state
  const currentHour = selectedDate ? selectedDate.getHours() : 12;
  const currentMinute = selectedDate ? selectedDate.getMinutes() : 0;
  const isPM = currentHour >= 12;
  const displayHour =
    currentHour === 0 ? 12 : currentHour > 12 ? currentHour - 12 : currentHour;

  // Quick date options
  const today = new Date();
  const yesterday = subDays(today, 1);
  const oneWeekAgo = subWeeks(today, 1);
  const twoWeeksAgo = subWeeks(today, 2);

  const isSameDay = (date1: Date | undefined, date2: Date) => {
    if (!date1) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const handleQuickSelect = (date: Date) => {
    // Preserve current time when quick-selecting a date
    if (selectedDate) {
      date = setHours(date, selectedDate.getHours());
      date = setMinutes(date, selectedDate.getMinutes());
    }
    setSelectedDate(date);
  };

  const handleHourChange = (hour: number) => {
    if (!selectedDate) return;
    const actualHour = isPM
      ? hour === 12
        ? 12
        : hour + 12
      : hour === 12
        ? 0
        : hour;
    setSelectedDate(setHours(selectedDate, actualHour));
  };

  const handleMinuteChange = (minute: number) => {
    if (!selectedDate) return;
    setSelectedDate(setMinutes(selectedDate, minute));
  };

  const handleAmPmToggle = () => {
    if (!selectedDate) return;
    const newHour = isPM ? currentHour - 12 : currentHour + 12;
    setSelectedDate(setHours(selectedDate, newHour));
  };

  const handleSave = async () => {
    if (!selectedDate) return;

    try {
      await patchMatter({
        matter_id: matterId,
        patch: {
          last_contacted_at_override: selectedDate.toISOString(),
        },
      }).unwrap();

      toast.success('Last contact date updated');
      setIsOpen(false);
    } catch {
      toast.error('Failed to update last contact date');
    }
  };

  const handleCancel = () => {
    // Reset to original value
    if (value) {
      const date = new Date(value);
      setSelectedDate(isValid(date) ? date : undefined);
    } else {
      setSelectedDate(undefined);
    }
    setIsOpen(false);
  };

  // Don't render if no display text
  if (!lastContactDisplayText) return null;

  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div>
          <Tooltip
            content={`Last contact: ${lastContactFullDate} (click to edit)`}
            side="top"
          >
            <InfoBadge
              label="Last Contact"
              value={`Last Contact: ${lastContactDisplayText}`}
              onClick={() => setIsOpen(true)}
              showLabel={false}
              className={lastContactStyles.className}
              valueClassName={lastContactStyles.valueClassName}
            />
          </Tooltip>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 shadow-lg"
        align="end"
        side="bottom"
        sideOffset={4}
      >
        {/* Quick Select */}
        <div className="px-2 pt-2 pb-1.5">
          <div className="flex items-center gap-1">
            <QuickDateButton
              label="Today"
              onClick={() => handleQuickSelect(today)}
              isSelected={isSameDay(selectedDate, today)}
            />
            <QuickDateButton
              label="Yesterday"
              onClick={() => handleQuickSelect(yesterday)}
              isSelected={isSameDay(selectedDate, yesterday)}
            />
            <QuickDateButton
              label="1 week ago"
              onClick={() => handleQuickSelect(oneWeekAgo)}
              isSelected={isSameDay(selectedDate, oneWeekAgo)}
            />
            <QuickDateButton
              label="2 weeks ago"
              onClick={() => handleQuickSelect(twoWeeksAgo)}
              isSelected={isSameDay(selectedDate, twoWeeksAgo)}
            />
          </div>
        </div>

        {/* Calendar */}
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          defaultMonth={selectedDate || today}
          disabled={{ after: today }}
          initialFocus
          className="pt-0 w-full"
        />

        {/* Time Picker */}
        <div className="px-2 py-1.5 border-t border-zinc-100 flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-500 font-medium">Time:</span>
          <TimeSelect
            value={displayHour}
            onChange={handleHourChange}
            options={hours}
          />
          <span className="text-zinc-400">:</span>
          <TimeSelect
            value={currentMinute}
            onChange={handleMinuteChange}
            options={minutes}
            formatFn={(v) => v.toString().padStart(2, '0')}
          />
          <button
            type="button"
            onClick={handleAmPmToggle}
            className="h-6 px-1.5 text-[10px] font-medium bg-zinc-100 rounded hover:bg-zinc-200 transition-colors"
          >
            {isPM ? 'PM' : 'AM'}
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-2 py-1.5 border-t border-zinc-100 bg-zinc-50/50">
          <span className="text-[10px] text-zinc-500">
            {selectedDate ? format(selectedDate, 'MMM d, h:mm a') : 'No date'}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isLoading}
              className="h-6 px-1.5 text-[10px] gap-0.5"
            >
              <X className="h-2.5 w-2.5" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isLoading || !selectedDate}
              className="h-6 px-1.5 text-[10px] gap-0.5 bg-zinc-900 hover:bg-zinc-800 text-white"
            >
              <Check className="h-2.5 w-2.5" />
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
